import { ReactNode } from 'react';
import { cn } from '../../lib/utils';

type BadgeVariant = 'default' | 'info' | 'success' | 'warning' | 'danger' | 'outline' | 'created' | 'progress' | 'completed' | 'paused' | 'failed' | 'overdue';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-700',
  info: 'bg-blue-100 text-blue-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-yellow-100 text-yellow-700',
  danger: 'bg-red-100 text-red-700',
  outline: 'bg-transparent border border-gray-300 text-gray-600',
  // Статусы задач
  created: 'bg-gray-100 text-gray-600',
  progress: 'bg-blue-500 text-white',
  completed: 'bg-green-500 text-white',
  paused: 'bg-yellow-500 text-white',
  failed: 'bg-red-100 text-red-700',
  overdue: 'bg-red-500 text-white',
};

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'text-[10px] px-2 py-0.5',
  md: 'text-xs px-3 py-1',
  lg: 'text-sm px-4 py-1.5',
};

export function Badge({ 
  children, 
  variant = 'default',
  size = 'md',
  className 
}: BadgeProps) {
  return (
    <span 
      className={cn(
        'inline-flex items-center rounded-full font-semibold whitespace-nowrap',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {children}
    </span>
  );
}

// Специальный компонент для статуса задачи
interface StatusBadgeProps {
  status: 'CREATED' | 'IN_PROGRESS' | 'PENDING_APPROVAL' | 'COMPLETED' | 'PAUSED' | 'FAILED' | 'NOT_SELECTED';
  isOverdue?: boolean;
  size?: BadgeSize;
}

const statusLabels: Record<string, string> = {
  CREATED: 'Поставлена',
  IN_PROGRESS: 'В процессе',
  PENDING_APPROVAL: 'Ожидает подтверждения',
  COMPLETED: 'Выполнена',
  PAUSED: 'Приостановлена',
  FAILED: 'Не выполнена',
  NOT_SELECTED: 'Не выбрана',
};

const statusVariants: Record<string, BadgeVariant> = {
  CREATED: 'created',
  IN_PROGRESS: 'progress',
  PENDING_APPROVAL: 'warning',
  COMPLETED: 'completed',
  PAUSED: 'paused',
  FAILED: 'failed',
  NOT_SELECTED: 'default',
};

export function StatusBadge({ status, isOverdue, size = 'sm' }: StatusBadgeProps) {
  if (isOverdue && status !== 'COMPLETED' && status !== 'PENDING_APPROVAL') {
    return (
      <Badge variant="overdue" size={size}>
        Просрочена
      </Badge>
    );
  }
  
  return (
    <Badge variant={statusVariants[status] || 'default'} size={size}>
      {statusLabels[status] || status}
    </Badge>
  );
}

// Компонент для приоритета
interface PriorityBadgeProps {
  priority: 'CRITICAL' | 'HIGH' | 'CURRENT';
  size?: BadgeSize;
}

export function PriorityBadge({ priority, size = 'sm' }: PriorityBadgeProps) {
  const styles = {
    CRITICAL: 'bg-red-500 text-white',
    HIGH: 'bg-orange-500 text-white',
    CURRENT: 'bg-green-500 text-white',
  };
  
  const labels = {
    CRITICAL: 'Сверхважная',
    HIGH: 'Важная',
    CURRENT: 'Текущая',
  };
  
  return (
    <span className={cn(
      'inline-flex items-center rounded-full font-semibold whitespace-nowrap',
      styles[priority],
      sizeStyles[size]
    )}>
      {labels[priority]}
    </span>
  );
}
